<!-- The chart and everything that belongs to it. The what-if panel is NOT here: it is declared at
     the bottom of this file as a template and rendered by the dialog in its own column beside the
     whole modal, so it is not boxed in by the chart's height. -->
<div class="rsc">

	<!-- Tiles collapse to a single strip so the plot can have the height instead. On a laptop the
	     tiles, chart and month brackets together do not fit, and scrolling a chart to reach its own
	     axis is the thing this whole view exists to avoid. -->
	<!-- Holds the tiles' row while the first figures are on their way, so the plot below does not
	     shrink when they arrive. -->
	<div class="rsc-tiles-wrap rsc-tiles-placeholder" *ngIf="!totals && busy && !errorMessage" aria-hidden="true">
		<!-- The real strip's own button, so the folded strip is the same height before and after the
		     figures arrive and the plot is drawn once. -->
		<button type="button" class="rsc-tiles-toggle" disabled tabindex="-1">
			<span class="rsc-caret" [class.rsc-caret-closed]="tilesCollapsed"></span>
			<span class="rsc-tiles-toggle-text">Totals</span>
		</button>
		<div class="rsc-tiles" *ngIf="!tilesCollapsed">
			<div class="rsc-tile">
				<span class="rsc-tile-label">Total</span>
				<span class="rsc-tile-value">&hellip;</span>
			</div>
		</div>
	</div>

	<div class="rsc-tiles-wrap" *ngIf="totals as t">
		<button type="button" class="rsc-tiles-toggle" (click)="toggleTiles()"
				[attr.aria-expanded]="!tilesCollapsed"
				[attr.title]="tilesCollapsed ? 'Show totals' : 'Hide totals'">
			<span class="rsc-caret" [class.rsc-caret-closed]="tilesCollapsed"></span>
			<span class="rsc-tiles-toggle-text">Totals</span>
		</button>

		<!-- Compare: each group's figure in both periods and the change between them. -->
		<div class="rsc-tiles" *ngIf="!tilesCollapsed && compare">
			<div class="rsc-tile rsc-compare-tile" *ngFor="let r of compareRows; trackBy: byRowName"
				 [ngStyle]="{ 'border-left': '3px solid ' + r.rgb }">
				<span class="rsc-tile-label">{{ r.name }}{{ averaged ? ' (average)' : '' }}</span>
				<span class="rsc-tile-value">{{ fmt(r.left) }} &rarr; {{ fmt(r.right) }} {{ unit }}</span>
				<!-- Neutral on purpose: less is good news for consumption and bad news for generation,
				     and the chart cannot tell which the reader wants. -->
				<span class="rsc-compare-change" *ngIf="r.change !== null">
					{{ r.change > 0 ? '+' : '' }}{{ r.change | number:'1.0-1' }}%
				</span>
				<span class="rsc-tile-trees" *ngIf="r.total && treesFor(r.right) as trees"
					  title="Estimated at 22 kg of CO2 absorbed by a mature tree in a year. Actual absorption varies by species, age and location.">
					{{ treesFor(r.left) | number:'1.0-0' }} &rarr; {{ trees | number:'1.0-0' }} mature trees a year
				</span>
			</div>
		</div>

		<div class="rsc-tiles" *ngIf="!tilesCollapsed && !compare">
			<!-- Gross, not net. A single "net" figure hides an export day completely: 812 kWh in
			     against 631 kWh out reads as 178 kWh and the export never appears. -->
			<div class="rsc-tile" *ngIf="t.grossImport !== null">
				<span class="rsc-tile-label">Imported from grid</span>
				<span class="rsc-tile-value">{{ fmt(t.grossImport) }} {{ unit }}</span>
			</div>

			<div class="rsc-tile" *ngIf="t.grossExport !== null">
				<span class="rsc-tile-label">Exported to grid</span>
				<span class="rsc-tile-value">{{ fmt(t.grossExport) }} {{ unit }}</span>
			</div>

			<!-- The SSRS remainder report's "Net Mains Import / Export" card. -->
			<div class="rsc-tile" *ngIf="t.netMains !== null">
				<span class="rsc-tile-label">Net mains</span>
				<span class="rsc-tile-value">{{ fmt(t.netMains) }} {{ unit }}</span>
			</div>

			<div class="rsc-tile" *ngIf="t.generated !== null">
				<span class="rsc-tile-label">Solar generated</span>
				<span class="rsc-tile-value">{{ fmt(t.generated) }} {{ unit }}</span>
			</div>

			<!-- Nothing exported in this window, so which group is the grid is unknown. Name each group
			     rather than labelling one of them "solar" on a guess. -->
			<!-- On a Combined chart, and past a handful of meters, these move to the meter list beside the plot (see meterList):
			     a tile each took two or three rows at Aran and Killeshal and squeezed the plot to a strip. -->
			<ng-container *ngIf="t.generated === null && !meterList">
				<div class="rsc-tile" *ngFor="let s of t.perSeries; trackBy: byRowName"
					 [ngStyle]="{ 'border-left': '3px solid ' + s.rgb }">
					<span class="rsc-tile-label">{{ s.name }}{{ averaged ? ' (average)' : '' }}</span>
					<span class="rsc-tile-value">{{ fmt(s.value) }} {{ unit }}</span>
				</div>
			</ng-container>

			<div class="rsc-tile" *ngIf="t.selfConsumed !== null">
				<span class="rsc-tile-label">Solar used on site</span>
				<span class="rsc-tile-value">{{ fmt(t.selfConsumed) }} {{ unit }}</span>
			</div>

			<!-- The chart's one total, named for what it is (see totals.ts): the site's consumption where
			     there is a grid, the mains meter on a remainder chart, and on a consumption chart every
			     meter shown, which counts a sub-meter and its mains twice. None for an average. -->
			<div class="rsc-tile" *ngIf="t.total !== null">
				<span class="rsc-tile-label">{{ t.totalLabel }}</span>
				<span class="rsc-tile-value">{{ fmt(t.total) }} {{ unit }}</span>
			</div>
		</div>

		<span class="rsc-tiles-note" *ngIf="t.note && !tilesCollapsed">{{ t.note }}</span>

		<!-- The chart is no longer showing metered figures, so say so wherever the panel is. The
		     wording is handed over with the scenario: the chart does not know what was changed. -->
		<span class="rsc-tiles-note" *ngIf="liveScenarioNote">{{ liveScenarioNote }}</span>

		<!-- Anjali's tree card from the SSRS carbon report, on the strip so folding the totals does not
		     hide it. See treeCard. -->
		<span class="rsc-trees" *ngIf="treeCard as tc" [matTooltip]="tc.how" tabindex="0">
			<mat-icon>forest</mat-icon>
			<span>About <b>{{ tc.trees | number:'1.0-0' }}</b> mature {{ tc.trees === 1 ? 'tree' : 'trees' }} a year to absorb this CO₂</span>
		</span>

		<!-- With the tiles folded, the drill's crumbs share their strip rather than taking a row of
		     their own: on a laptop with the what-if open that row was the plot's. -->
		<ng-container *ngIf="tilesCollapsed" [ngTemplateOutlet]="crumbs"></ng-container>
	</div>

	<p class="rsc-msg rsc-error" *ngIf="errorMessage">{{ errorMessage }}</p>

	<!-- Where you are in the drill. Always shown once you have stepped in, because a chart of hours
	     and a chart of months look alike at a glance and there is otherwise nothing saying which
	     you are looking at. Each crumb steps back to that level. -->
	<ng-container *ngIf="!tilesCollapsed || !totals" [ngTemplateOutlet]="crumbs"></ng-container>
	<ng-template #crumbs>
		<div class="rsc-crumbs" *ngIf="drillStack.length">
			<button type="button" class="rsc-crumb-up" (click)="drillUp()" title="Back up one level">&larr;</button>
			<ng-container *ngFor="let c of drillCrumbs; let last = last">
				<button type="button" class="rsc-crumb" [class.rsc-crumb-here]="last"
						[disabled]="last" (click)="drillToDepth(c.depth)">{{ c.label }}</button>
				<span class="rsc-crumb-sep" *ngIf="!last">/</span>
			</ng-container>
		</div>
	</ng-template>

	<!-- The dialog's T box. The SSRS toolbar exported and printed; this does the same for what is
	     on screen, drilled or not. -->
	<div class="rsc-toolbar" *ngIf="showToolbar && !errorMessage && chartData.length">
		<button type="button" class="rsc-tool" (click)="exportCsv()" title="Download the figures as a CSV file, which opens in Excel">Download data (CSV)</button>
		<button type="button" class="rsc-tool" (click)="exportPng()" title="Download the chart as a picture">Download image</button>
		<button type="button" class="rsc-tool" (click)="printChart()" title="Print the chart, or save it as a PDF">Print</button>
		<span class="rsc-msg" *ngIf="exportMessage">{{ exportMessage }}</span>
	</div>

	<p class="rsc-msg" *ngIf="drillUnavailableReason">{{ drillUnavailableReason }}</p>

	<!-- Takes whatever height the tiles are not using, so collapsing them grows the plot instead
	     of leaving a gap.

	     There while loading too, empty on the first load and holding the last chart on a reload,
	     with "Loading..." over it. It used to be taken off the page for every load and put back,
	     which is what made the box shrink and grow on opening and on every change of meter. Being
	     there from the start also means it is measured before the first bar is drawn, rather than
	     the bars appearing at a 600x300 default and jumping to size a frame later. -->
	<!-- The plot, and beside it the meter list where the meters are listed (see meterList). -->
	<div class="rsc-plot-row" *ngIf="!errorMessage && (chartData.length || busy)">
	<div class="rsc-chart" [class.rsc-chart-drillable]="canDrill && !busy" [class.rsc-chart-busy]="busy"
		 [class.rsc-highlighting]="!!highlighted" role="figure" [attr.aria-label]="chartTitle || axisTitle">
		<div class="rsc-busy" *ngIf="busy" data-export="skip">Loading...</div>
		<ng-container *ngIf="chartData.length">
		<!-- One of three, following the SSRS report this replaces. See chartStyle.

		     [@.disabled] on every chart: [animations]="false" does not reach ngx-charts' own :leave
		     transition on a bar group, which keeps each old bar on the page for half a second after
		     the data changes. Drilling a week into Sunday showed the week's days over Sunday's hours
		     for that long, and a test reading the bars in that moment found 31 across 7 days. -->
		<ngx-charts-bar-vertical-stacked *ngIf="chartStyle === 'stacked'" [@.disabled]="true"
			[results]="chartData"
			[view]="chartView"
			[animations]="false"
			[scheme]="colours"
			[legend]="legend"
			[xAxis]="xAxis"
			[xAxisTickFormatting]="tickFormat"
			[xAxisTicks]="axisTicks!"
			[activeEntries]="activeEntries"
			[yAxis]="yAxis"
			[showGridLines]="showGridLines"
			[roundDomains]="roundDomains"
			[yAxisLabel]="axisTitle"
			[showYAxisLabel]="true"
			[xAxisLabel]="periodLabel"
			[showXAxisLabel]="!!periodLabel && !spansMonths"
			[showDataLabel]="false"
			[barPadding]="barGap"
			[yScaleMax]="yScaleMax!"
			[tooltipDisabled]="columnTips"
			(select)="onBarSelect($event)">
			<!-- ngx-charts' tooltip for one segment, in the chart's own words (see tip()). -->
			<ng-template #tooltipTemplate let-model="model">
				<span class="tooltip-label">{{ tip(model).title }} • {{ tip(model).name }}</span>
				<span class="tooltip-val">{{ tip(model).value }}</span>
			</ng-template>
		</ngx-charts-bar-vertical-stacked>

		<ngx-charts-bar-vertical-2d *ngIf="chartStyle === 'grouped'" [@.disabled]="true"
			[results]="chartData"
			[view]="chartView"
			[animations]="false"
			[scheme]="colours"
			[legend]="legend"
			[xAxis]="xAxis"
			[xAxisTickFormatting]="tickFormat"
			[xAxisTicks]="axisTicks!"
			[activeEntries]="activeEntries"
			[yAxis]="yAxis"
			[showGridLines]="showGridLines"
			[roundDomains]="roundDomains"
			[yAxisLabel]="axisTitle"
			[showYAxisLabel]="true"
			[xAxisLabel]="periodLabel"
			[showXAxisLabel]="!!periodLabel && !spansMonths"
			[showDataLabel]="false"
			[groupPadding]="groupPadding"
			[barPadding]="groupedBarGap"
			[trimXAxisTicks]="!compare"
			[tooltipDisabled]="columnTips"
			(select)="onBarSelect($event)">
			<!-- ngx-charts' tooltip for one segment, in the chart's own words (see tip()). -->
			<ng-template #tooltipTemplate let-model="model">
				<span class="tooltip-label">{{ tip(model).title }} • {{ tip(model).name }}</span>
				<span class="tooltip-val">{{ tip(model).value }}</span>
			</ng-template>
		</ngx-charts-bar-vertical-2d>

		<ngx-charts-line-chart *ngIf="chartStyle === 'line'" [@.disabled]="true"
			[results]="lineData"
			[view]="chartView"
			[animations]="false"
			[scheme]="colours"
			[legend]="legend"
			[xAxis]="xAxis"
			[xAxisTickFormatting]="tickFormat"
			[xAxisTicks]="axisTicks!"
			[activeEntries]="activeEntries"
			[yAxis]="yAxis"
			[showGridLines]="showGridLines"
			[roundDomains]="roundDomains"
			[yAxisLabel]="axisTitle"
			[showYAxisLabel]="true"
			[xAxisLabel]="periodLabel"
			[showXAxisLabel]="!!periodLabel && !spansMonths"
			[autoScale]="true"
			[curve]="curve"
			(select)="onBarSelect($event)">
			<!-- The point under the pointer, and the line through all of them, as the bars say them. -->
			<ng-template #tooltipTemplate let-model="model">
				<span class="tooltip-label">{{ tip(model).title }} • {{ tip(model).name }}</span>
				<span class="tooltip-val">{{ tip(model).value }}</span>
			</ng-template>
			<ng-template #seriesTooltipTemplate let-model="model">
				<div class="area-tooltip-container">
					<div class="tooltip-item"><b>{{ tip(model[0]).title }}</b></div>
					<div *ngFor="let m of model" class="tooltip-item">
						<span class="tooltip-item-color" [style.background-color]="m.color"></span>
						{{ tip(m).name }}: {{ tip(m).value }}
					</div>
				</div>
			</ng-template>
		</ngx-charts-line-chart>

		<!-- Pinned under the month brackets. ngx-charts' own legend renders inside the plot box,
		     which put it above them. -->
		<div class="rsc-legend" *ngIf="legendItems.length && !meterList">
			<span class="rsc-legend-item" *ngFor="let l of legendItems">
				<i class="rsc-legend-swatch" [ngStyle]="{ background: l.rgb }"></i>{{ l.name }}
			</span>
		</div>

		<!-- Compare: a pie for each period, as the SSRS Compare reports draw them. -->
		<div class="rsc-compare-pies" *ngIf="compare && comparePeriods.length === 2">
			<div class="rsc-compare-pie" *ngFor="let name of comparePeriods; let i = index">
				<div class="rsc-compare-pie-title">{{ name }}</div>
				<ngx-charts-pie-chart [@.disabled]="true"
					[results]="comparePies[i]"
					[view]="pieView"
					[animations]="false"
					[customColors]="pieColours"
					[doughnut]="true"
					[labels]="false"
					[legend]="false">
				</ngx-charts-pie-chart>
				<ul class="rsc-compare-legend">
					<li *ngFor="let l of compareLegends[i]; trackBy: byRowName">
						<i class="rsc-legend-swatch" [style.background]="l.rgb"></i>
						<span class="rsc-compare-legend-name">{{ l.name }}</span>
						<b>{{ l.value }}</b>
					</li>
				</ul>
			</div>
		</div>

		<!-- Says what a click will actually do at this level, rather than just that one is possible.
		     Nothing is drawn at the hour level, which is the floor. -->
		<p class="rsc-drill-hint" data-export="skip" *ngIf="drillNextLabel">
			Click a {{ chartStyle === 'line' ? 'point' : 'bar' }} to see its {{ drillNextLabel }}.
		</p>
		</ng-container>
	</div>

	<!-- Tiles and legend in one: each meter's colour, name, total and share, biggest first. Hovering a
	     row fades every other meter in the plot. -->
	<aside class="rsc-meters" *ngIf="meterList && chartData.length">
		<div class="rsc-meters-head">
			<span>Meter</span>
			<span>{{ averaged ? 'Average' : unit }}</span>
			<span *ngIf="showShares">Share</span>
		</div>
		<div class="rsc-meters-list">
			<div class="rsc-meter" *ngFor="let m of meterRows; trackBy: byName" [attr.title]="m.name"
				 (mouseenter)="highlightSeries(m.name)" (mouseleave)="highlightSeries(null)">
				<i class="rsc-legend-swatch" [style.background]="m.rgb"></i>
				<span class="rsc-meter-name">{{ m.name }}</span>
				<span class="rsc-meter-value">{{ m.value === null ? '' : fmt(m.value) }}</span>
				<span class="rsc-meter-share" *ngIf="showShares">{{ m.share === null ? '' : (m.share | percent:'1.0-0') }}</span>
			</div>
		</div>
	</aside>
	</div>

	<p class="rsc-msg" *ngIf="!busy && !errorMessage && !chartData.length">
		No readings for this period.
	</p>

</div>

<!-- The what-if lives in its own component now. This template is only the seam the dialog
     renders through: it is declared here and placed by modal-reports, beside the whole modal
     rather than inside the chart's column, so the panel is not boxed in by the chart's height. -->
<ng-template #whatIfTpl>
	<app-what-if-panel
		[rawSeries]="rawSeries"
		[solarConfig]="solarConfig"
		[tariffRates]="tariffRates"
		[siteId]="siteId"
		[siteMic]="siteMic"
		[solarEstimateNote]="solarEstimateNote"
		[solarYearByDay]="solarYearByDay"
		[measured]="measured"
		[hourlyRows]="hourlyRows"
		[hourlyLoaded]="hourlyLoaded"
		[batteryUnavailableReason]="hourlyUnavailableReason"
		[bucketDates]="bucketDates"
		[bucketWindowEnd]="bucketWindowEnd"
		[totals]="totals"
		[baseTotals]="baseTotals"
		[unit]="unit"
		[intervalCap]="intervalCap"
		[gridLogicalGroupId]="gridLogicalGroupId"
		[scaleOptions]="scaleOptions"
		(scenarioChange)="onScenario($event)"
		(closed)="closeWhatIf()">
	</app-what-if-panel>
</ng-template>
